import type { FromBaseRow as Row } from './from-base-context';
import { fromBaseRecord, fromBaseUuid } from './from-base-context';
import type { FormulaVariableReaders } from './formula-variable-resolution';
import type { ItemFieldRow } from './item-field-reader';
import { readLayerListEntryUuid } from './inventory-raw-readers';
import { layerEntryNeedsResolve } from './layer-entry-resolver';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { ftZeroVitalityWitnessFact } from '../port/k4-dispatch/zero_vitality_witness_triggers';
import type { E } from '../port/k4-dispatch/support-b2';

const carriers = ['passive_ability', 'reaction_ability', 'free_ability', 'menta', 'curse',
    'at_will_spell', 'innate_spell_per_long_rest', 'spells_known', 'opportunity_attack_ability'];

/** Native constructor facts shared by PCs and live NPCs. */
export function fromBaseReachWatchFacts(host: Row, readers: FormulaVariableReaders, definition: (id: string) => Row | null,
    types: readonly string[] = carriers): Row | undefined {
    const stored = (row: ItemFieldRow, key: string) => readers.itemField({ [key]: [(row as Row)[key] ?? null] }, key);
    const entries = (row: ItemFieldRow, key: string) => Object.values(readers.layerEntries(row, key));
    const has = (row: ItemFieldRow, key: string) => readers.layerValue(row, key) !== null || entries(row, key).length > 0;
    const restore = (entry: unknown): unknown => {
        if (!fromBaseRecord(entry)) return entry;
        const nested = stored(entry, 'item_arr');
        return fromBaseUuid(nested) ? { ...entry, item_arr: definition(nested) } : entry;
    };
    if (!phpJsonTruthy(readers.itemField(fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : null, 'encounter_creature_id'))) return;
    const catalog = stored(host, 'catalog'), definitions = new Map<string, Row | null>();
    const lookup = (id: string): Row | null => {
        if (!definitions.has(id)) definitions.set(id, definition(id));
        return definitions.get(id)!;
    };
    const deps: Partial<E['deps']> = {
        layer_has: (row, key) => has(row as ItemFieldRow, phpJsonString(key)),
        layer_value: (row, key) => readers.layerValue(row as ItemFieldRow, phpJsonString(key)),
        layer_entries: (row, key) => readers.layerEntries(row as ItemFieldRow, phpJsonString(key)),
        item_field: (row, key) => readers.itemField(row as ItemFieldRow | null, phpJsonString(key)),
        check_uuid: fromBaseUuid, janus_trigger_carriers: () => types,
        ft_entry_restore_item_arr: restore, ft_layer_entry_needs_resolve: layerEntryNeedsResolve,
        layer_list_entry_uuid: readLayerListEntryUuid,
        ft_catalog_def: (_root, id) => fromBaseRecord(catalog) ? catalog[phpJsonString(id)] ?? null : null,
        query_full_item_arr: id => lookup(phpJsonString(id)),
    };
    const watch: Row = { enters_reach: false, target_moves: false, leaves_reach: false, unresolved: 0,
        zero_vitality_witness_ft: ftZeroVitalityWitnessFact(host, { deps } as E), schema: 'ft.reach-watch.v1' };
    for (const type of types) {
        const root = stored(host, type);
        for (const raw of [...entries(host, type), ...(root !== null && typeof root === 'object' ? Object.values(root) : [])]) {
            const entry = restore(raw);
            if (typeof entry === 'string') { if (fromBaseUuid(entry)) watch.unresolved = phpJsonInt(stored(watch, 'unresolved')) + 1; continue; }
            if (!fromBaseRecord(entry)) continue;
            const nested = stored(entry, 'item_arr');
            const ability = fromBaseRecord(nested) ? nested : readers.itemField(entry, 'item_id') ? entry : null;
            if (!ability) continue;
            if (layerEntryNeedsResolve(ability)) {
                if (!['at_will_spell', 'innate_spell_per_long_rest', 'spells_known'].includes(type)) watch.unresolved = phpJsonInt(stored(watch, 'unresolved')) + 1;
                continue;
            }
            for (const [key, layer] of [['enters_reach', 'triggers_when_target_enters_reach'], ['target_moves', 'triggers_when_target_moves'], ['leaves_reach', 'triggers_when_target_leaves_reach']] as const)
                if (has(ability, layer)) watch[key] = true;
        }
    }
    return watch;
}

export function constructFromBaseReachWatch(host: Row, readers: FormulaVariableReaders, definition: (id: string) => Row | null): void {
    const watch = fromBaseReachWatchFacts(host, readers, definition);
    if (!watch) return;
    const computed = host.computed as Row;
    (computed.creature as Row).reach_watch = watch;
    // Independent carrier facts are inputs to the spell cluster, not a normalized-holder rewalk.
    if (fromBaseRecord(host.player_characters)) {
        (computed._cache as Row).spell_watch_base = fromBaseReachWatchFacts(host, readers, definition, carriers.filter(type => type !== 'spells_known'));
    }
}
